import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Container } from 'react-bootstrap';
import { Formik, FormikValues, yupToFormErrors } from 'formik';
import { merge, isEqual } from 'lodash';

import history from 'browserHistory';
import { useModal } from 'modules/shared/hooks';
import { deleteTab } from 'modules/common/Tabs/actions';
import { cleanTripFields } from 'modules/common/FillTrip';
import FormikPatchTouched from 'modules/shared/Forms/FormikPatchTouched';
import FormikFillFields from 'modules/shared/Forms/FormikFillFields';
import FlightTop from 'modules/shared/FlightTop';
import FlightDetails from 'modules/shared/FlightDetails';
import ModalWrapper from 'modules/shared/ModalWrapper';
import YesNoModal from 'modules/shared/YesNoModal';
import ErrorMsg from 'modules/shared/ErrorMsg';
import { usePrepareValues } from 'modules/common/CommonTrip/hooks/usePrepareValues';
import { useValidationSchema } from 'modules/common/CommonTrip/hooks/useValidationSchema';
import DepartureData from 'modules/shared/DepartureData';
import Loader from 'modules/kiosk/components/Loader';
import RegOfDocuments from 'modules/shared/RegOfDocuments';
import DepartureComment from 'modules/shared/DepartureComment';
import { peripheralsSelector } from 'modules/common/Header/selectors';
import { branchTitleSelector } from 'modules/common/LogoutBtn/selectors';
import { commonTripSelector } from 'modules/common/CommonTrip/selectors';
import { loadingSelector } from 'modules/security/containers/TripContainer/services/selectors';
import { cleanError, getTripRequest, updateTripRequest, updateTripStatus } from 'modules/common/CommonTrip/actions';
import { RootState } from 'rootReducer';
import { tripStatuses, tripIntentionStatuses } from '../../../../entities/Trip/constants';
import styles from '../../../ordering/containers/TripContainer/TripContainer.module.scss';

type Params = {
  tripId: string;
};

const TripContainer: React.FC = () => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const { tripId = '' } = useParams<Params>();
  const [allowedExitRealTolerance, setAllowedExitRealTolerance] = useState<number>(0);
  const [allowedExitTheoreticalTolerance, setAllowedExitTheoreticalTolerance] = useState<number>(0);

  const { isShow, openModal, closeModal } = useModal();
  const { isShow: isShowYN, openModal: openYN, closeModal: closeYN } = useModal();
  const { isShow: isShowWeightYN, openModal: openWeightYN, closeModal: closeWeightYN } = useModal();
  const { isShow: isShowErr, openModal: openErrToast, closeModal: closeErrToast } = useModal();

  const [tripDataReq, setTripDataReq] = useState<any>();
  const { loadingTrip, errorTrip, trip } = useSelector(commonTripSelector);

  const role = useSelector((state: RootState) => state.authModuleReducer.authReducer.role);

  const { id, status, ...rest } = trip;
  const statusId = status?.id || 2;

  const statusPermision = false; //  status?.id === 1 || status?.id === 99 || status?.id === 10;

  const loadingStatus = useSelector(loadingSelector);
  const peripherals = useSelector(peripheralsSelector);

  const initValues = usePrepareValues(id, rest);

  const operatingProcedure = useMemo(() => {
    if (role?.branches && role.branches.length !== 0) {
      return role.branches.find((branch) => branch?.id === JSON.parse(localStorage.getItem('branchId') || 'null')?.id)
        ?.operatingProcedure;
    }

    return null;
  }, [role]);

  useEffect(() => {
    const realTolerance =
      peripherals?.find((per) => isEqual(per.type, 'Scales') && isEqual(per.processName, 'Exit'))
        ?.allowedExitTolerance || 0;
    const theoreticalTolerance =
      peripherals?.find((per) => isEqual(per.type, 'Scales') && isEqual(per.processName, 'Exit'))
        ?.allowedExitTheoreticalTolerance || 0;

    setAllowedExitRealTolerance(realTolerance);
    setAllowedExitTheoreticalTolerance(theoreticalTolerance);
  }, [peripherals, id]);

  // @ts-ignore
  const intentionForm = formRef.current?.values?.tripIntention;

  const loading = +intentionForm === tripIntentionStatuses.loading;
  const branchTitle = useSelector(branchTitleSelector);
  const useVisitorDoc: boolean = branchTitle?.useVisitorDoc ?? true;
  const useTransportDoc: boolean = branchTitle?.useTransportDoc ?? true;
  const validationSchema = useValidationSchema(
    'security',
    statusId,
    loading,
    operatingProcedure ?? '',
    useVisitorDoc,
    useTransportDoc,
  );

  const allowWeight = rest?.allowedWeight || 0;

  const closeTabHandler = () => {
    const timer = setTimeout(() => {
      dispatch(deleteTab(`trip_${tripId}`));
      closeModal();

      return history.push('/director');
    }, 3000);

    clearTimeout(timer);
    dispatch(deleteTab(`trip_${tripId}`));
    closeModal();

    return history.push('/director');
  };
  const onWeighing = () => {
    dispatch(updateTripStatus(trip));
    return closeTabHandler();
  };

  const regDepartureHandler = () => {
    closeYN();
    dispatch(updateTripRequest(tripDataReq, false, status?.id));
    dispatch(cleanTripFields());
    openModal();

    return closeTabHandler();
  };

  useEffect(() => {
    if (tripId && !id) {
      dispatch(getTripRequest(tripId));
    }
  }, [id]);

  useEffect(() => {
    if (errorTrip) {
      return openErrToast();
    }
    closeErrToast();
  }, [errorTrip]);

  const validate = (v: any) => {
    try {
      validationSchema.validateSync(v, { abortEarly: false, context: { values: v } });
      return {};
    } catch (error) {
      // @ts-igonre
      console.log((error as any)?.errors);
      return yupToFormErrors<FormikValues>(error);
    }
  };

  return (
    <Container>
      {(loadingStatus || loadingTrip || (!id && !errorTrip)) && <Loader />}
      {!loadingStatus && !loadingTrip && id && !statusPermision ? (
        <Formik
          // @ts-ignore
          innerRef={formRef}
          initialValues={initValues}
          // validationSchema={validationSchema}
          validate={validate}
          validateOnMount
          enableReinitialize
          onSubmit={(values) => {
            const tripData = merge({ id }, values);
            if (values.orders.length === 0) {
              tripData.statusId = tripStatuses.parkingInLine;
              setTripDataReq(tripData);
              openYN();
            } else {
              tripData.statusId = tripStatuses.waitingDeparture;
              setTripDataReq(tripData);
              openYN();
            }
          }}
        >
          {({ values, handleChange, handleSubmit, setFieldValue, isValid, errors }) => (
            <form onSubmit={handleSubmit} className="mt-4">
              <FormikPatchTouched />
              <FormikFillFields />
              <FlightTop
                status={status}
                tripId={id}
                handleChange={handleChange}
                tripIntention={values.tripIntention}
                hideTripIntention
              />
              {values?.orders?.length &&
                (values.tripIntention === 1 ||
                  (operatingProcedure !== 'Порядок 2' && operatingProcedure !== 'Порядок 3')) && (
                  <RegOfDocuments mode="director" handleChange={handleChange} setFieldValue={setFieldValue} />
                )}
              <FlightDetails
                mode="director"
                statusId={statusId}
                tripType="driver"
                handleChange={handleChange}
                operatingProcedure={operatingProcedure ?? ''}
              />
              <FlightDetails
                mode="director"
                statusId={statusId}
                tripType="vehicle"
                handleChange={handleChange}
                operatingProcedure={operatingProcedure ?? ''}
              />
              <FlightDetails
                mode="director"
                statusId={statusId}
                tripType="forwarder"
                handleChange={handleChange}
                operatingProcedure={operatingProcedure ?? ''}
              />
              <DepartureData
                mode="director"
                statusId={statusId}
                values={values}
                errors={errors}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                allowWeight={allowWeight}
                allowedExitRealTolerance={allowedExitRealTolerance}
                allowedExitTheoreticalTolerance={allowedExitTheoreticalTolerance}
                operatingProcedure={operatingProcedure ?? ''}
              />
              <DepartureComment mode="director" statusId={statusId} handleChange={handleChange} errors={errors} />
              {values.statusId === 8 ? (
                <div className="d-flex justify-content-end mt-4 mb-5">
                  <Button
                    type="button"
                    className="px-3 text-uppercase mr-4"
                    variant="danger"
                    disabled={values.allowWithTolerance || values.orders.length === 0}
                    onClick={openWeightYN}
                  >
                    <h5 className="my-2 text-white mx-5">на перезважування</h5>
                  </Button>
                  <Button
                    type="submit"
                    className="px-3 text-uppercase"
                    variant="danger"
                    disabled={!values.orders || !isValid}
                  >
                    <h5 className="my-2 text-white mx-5">дозволити виїзд</h5>
                  </Button>
                </div>
              ) : null}
            </form>
          )}
        </Formik>
      ) : null}
      {statusPermision ? (
        <h3 className="m-5 text-center">
          Порушення доступу до деталей рейсу.{' '}
          <button
            className={styles.button}
            onClick={() => {
              dispatch(deleteTab(`trip_${tripId}`));
              history.push('/director');
            }}
            type="button"
          >
            <h3 className={styles.text}>На головну</h3>
          </button>
        </h3>
      ) : null}
      <ModalWrapper show={isShow} onHide={closeTabHandler}>
        <div>
          <h6 className="text-center">Успішно</h6>
          <div className="d-flex justify-content-center mt-3">
            <Button className="px-5" variant="outline-secondary" onClick={closeTabHandler}>
              Ok
            </Button>
          </div>
        </div>
      </ModalWrapper>
      <YesNoModal
        isShow={isShowYN}
        title={`Рейс ${tripId} поставити в чергу`}
        acceptHandler={regDepartureHandler}
        closeModal={closeYN}
      />
      <YesNoModal
        isShow={isShowWeightYN}
        title={`Рейс ${tripId} відправити на перезважування`}
        acceptHandler={onWeighing}
        closeModal={closeWeightYN}
      />
      <ErrorMsg
        isShow={isShowErr}
        closeHandler={() => {
          closeErrToast();
          dispatch(cleanError());
        }}
        error={errorTrip?.message || 'Виникла помилка'}
      />
    </Container>
  );
};

export default TripContainer;
